<template>
    <section
        id="journey-settings-panel-savedJourneys"
        aria-labelledby="journey-settings-tab-savedJourneys"
        role="tabpanel"
        class="space-y-6"
    >
        <section class="space-y-3">
            <h2 class="font-semibold">Saved journeys</h2>
            <p v-if="journeys.length === 0" class="text-sm text-ink-subtle">No saved journeys.</p>
            <div
                v-for="(journey, index) in journeys"
                :key="journey.id"
                class="rounded-lg border border-line bg-paper p-3"
                data-test="saved-journey"
            >
                <div class="flex items-start justify-between gap-2">
                    <JourneyLabel
                        v-if="journeyReferencesExistingGroups(journey, stationGroups)"
                        :details="getJourneyLabelDetails(journey, stationGroups)"
                    />
                    <span v-else>Choose journey stations</span>
                    <div class="flex shrink-0 gap-2">
                        <AppIconButton
                            class="appButton appButton--icon text-ink hover:text-paper hover:bg-ink"
                            @click="
                                editingJourneyId =
                                    editingJourneyId === journey.id
                                        ? undefined
                                        : journey.id
                            "
                            :label="editingJourneyId === journey.id ? 'Done editing' : 'Edit journey'"
                            :icon="editingJourneyId === journey.id ? 'close' : 'pencil'"
                        />
                        <button
                            class="appButton appButton--icon text-danger hover:text-paper hover:bg-danger"
                            type="button"
                            :disabled="getScheduleNamesThatUseJourney(journey.id, schedules).length > 0"
                            title="Remove journey"
                            aria-label="Remove journey"
                            @click="removeSavedJourney(journey.id)"
                        >
                            <AppIcon class="size-4" name="trash" />
                        </button>
                    </div>
                </div>
                <p
                    v-if="getScheduleNamesThatUseJourney(journey.id, schedules).length"
                    class="mt-2 text-xs text-ink-subtle"
                >
                    Used by: {{ getScheduleNamesThatUseJourney(journey.id, schedules).join(", ") }}. Change these schedules before removing this journey.
                </p>
                <JourneySettingsFields
                    v-if="editingJourneyId === journey.id"
                    v-model:journey="journeys[index]!"
                    class="mt-3"
                    :stationGroups="stationGroups"
                    :journeys="journeys"
                    :scheduleNames="[]"
                    @changed="emit('changed')"
                />
            </div>
        </section>
        <section class="space-y-3">
            <h2 class="font-semibold">Recent journeys</h2>
            <p v-if="recentJourneys.length === 0" class="text-sm text-ink-subtle">
                No recent journeys.
            </p>
            <div
                v-for="journey in recentJourneys"
                :key="journey.id"
                class="flex items-start justify-between gap-2 border-t border-line pt-3"
                data-test="recent-journey"
            >
                <JourneyLabel
                    v-if="journeyReferencesExistingGroups(journey, stationGroups)"
                    :details="getJourneyLabelDetails(journey, stationGroups)"
                />
                <span v-else>Choose journey stations</span>
                <div class="flex shrink-0 items-center gap-2">
                    <span
                        v-if="isSaved(journey)"
                        class="flex size-8 items-center justify-center text-ink-subtle"
                        title="Saved journey"
                    >
                        <AppIcon class="size-4 fill-current" name="bookmark" />
                        <span class="sr-only">Saved journey</span>
                    </span>
                    <AppIconButton
                        v-else
                        class="appButton appButton--icon size-8 text-ink hover:text-paper hover:bg-ink"
                        @click="saveRecentJourney(journey)"
                        label="Save journey"
                        icon="bookmark"
                    />
                    <AppIconButton
                        class="appButton appButton--icon size-8 text-danger hover:text-paper hover:bg-danger"
                        @click="forgetRecentJourney(journey.id)"
                        label="Forget journey"
                        icon="close"
                    />
                </div>
            </div>
        </section>
    </section>
</template>

<script setup lang="ts">
import AppIconButton from "@/components/AppIconButton.vue";
import AppIcon from "@/components/AppIcon.vue";
import {computed, ref} from "vue";
import {journeyReferencesExistingGroups, journeysAreTheSame} from "../../../journeys/journeyIdentity";
import type {Journey} from "../../../dto/journey.dto";
import type {DisplaySchedule} from "../../../dto/displaySchedule.dto";
import type {StationGroup} from "../../../dto/stationGroup.dto";
import {getJourneyLabelDetails} from "../../../journeys/journeyLabels";
import {useJourneySelectionStore} from "../../../store/journeySelection.store";
import JourneyLabel from "../../journeys/JourneyLabel.vue";
import JourneySettingsFields from "./JourneySettingsFields.vue";
import {getScheduleNamesThatUseJourney} from "../schedules/scheduleSettings";

const props = defineProps<{
    stationGroups: StationGroup[];
    schedules: DisplaySchedule[];
}>();
const journeys = defineModel<Journey[]>("journeys", {required: true});
const recentJourneyIds = defineModel<string[]>("recentJourneyIds", {
    required: true,
});
const emit = defineEmits<{changed: []}>();
const journeySelectionStore = useJourneySelectionStore();
const editingJourneyId = ref<string>();
const recentJourneys = computed(() => {
    const journeysById = new Map<string, Journey>();
    for (const journey of [...journeySelectionStore.recentEphemeralJourneys, ...journeys.value]) {
        journeysById.set(journey.id, journey);
    }
    return recentJourneyIds.value.flatMap((id) => {
        const journey = journeysById.get(id);
        return journey && journeyReferencesExistingGroups(journey, props.stationGroups)
            ? [journey]
            : [];
    });
});

function isSaved(journey: Journey): boolean {
    return journeys.value.some(
        (saved) => saved.id === journey.id || journeysAreTheSame(saved, journey)
    );
}

function saveRecentJourney(journey: Journey): void {
    if (isSaved(journey)) {
        return;
    }
    journeys.value.push(JSON.parse(JSON.stringify(journey)));
    emit("changed");
}

function forgetRecentJourney(journeyId: string): void {
    recentJourneyIds.value = recentJourneyIds.value.filter((id) => id !== journeyId);
    emit("changed");
}

function removeSavedJourney(journeyId: string): void {
    const journeyIsUsedInSchedule = getScheduleNamesThatUseJourney(journeyId, props.schedules).length > 0;
    if (journeyIsUsedInSchedule) {
        return;
    }
    journeys.value = journeys.value.filter((journey) => journey.id !== journeyId);
    forgetRecentJourney(journeyId);
}
</script>
